import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, type LoginRequest, type RegisterRequest } from '@dpem/contracts';
import { useQueryClient } from '@tanstack/react-query';

import { useApi } from './ApiProvider';
import { capabilitySetFor, type CapabilitySet } from '@/lib/auth/capabilities';
import {
  useSessionStore,
  type SessionStatus,
  type SessionUser,
} from '@/lib/auth/session.store';

/**
 * The session's lifecycle: restore it at boot, keep it alive, end it cleanly.
 *
 * Three things happen here and nowhere else.
 *
 * **Restore.** The access token lives in memory, so a reload has none. The
 * refresh cookie survives, so boot spends one request asking the API to mint a
 * new token from it. That request failing is not an error — it is what a signed
 * out visitor looks like.
 *
 * **Keep alive.** A token refreshed *after* it expires costs the user a failed
 * request and, if they were mid-edit, a confusing error. So it is refreshed
 * before it expires, on a timer derived from `expiresIn`. Reactive
 * refresh-on-401 is deliberately not implemented: it means every in-flight
 * request during the expiry window fails once and has to be replayed, and
 * replaying a mutation is how you publish a method twice.
 *
 * **End.** Logout revokes server-side, then clears the local token and the whole
 * query cache. Clearing the cache is not tidiness: a cache holding one user's
 * methods when a second user signs in on the same machine is a data leak.
 */

/** Refresh this long before expiry — enough for a slow network, not so early it doubles the request rate. */
const REFRESH_MARGIN_MS = 60_000;
/** Never schedule a refresh tighter than this, or a short-lived token spins. */
const MIN_REFRESH_DELAY_MS = 5_000;

export interface AuthApi {
  readonly status: SessionStatus;
  readonly user: SessionUser | null;
  readonly capabilities: CapabilitySet;
  /** Set when the last session ended because the server rejected it. */
  readonly sessionExpired: boolean;
  readonly login: (credentials: LoginRequest) => Promise<void>;
  readonly register: (registration: RegisterRequest) => Promise<void>;
  readonly logout: () => Promise<void>;
  /** True while a login, registration or logout is in flight. */
  readonly pending: boolean;
}

const AuthContext = createContext<AuthApi | null>(null);

export const useAuth = (): AuthApi => {
  const auth = useContext(AuthContext);
  if (auth === null) throw new Error('useAuth must be used inside <AuthProvider>');
  return auth;
};

/** Convenience for the common case: what may this user do? */
export const useCapabilities = (): CapabilitySet => useAuth().capabilities;

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Injected so tests own the clock. Production passes nothing and gets
   * `Date.now`; the store never reads a clock itself.
   */
  now?: () => number;
  /** Skip the boot refresh. Tests that seed a session directly do not want it. */
  restoreOnMount?: boolean;
}

export function AuthProvider({ children, now = Date.now, restoreOnMount = true }: AuthProviderProps) {
  const api = useApi();
  const queryClient = useQueryClient();

  const status = useSessionStore((state) => state.status);
  const user = useSessionStore((state) => state.user);
  const roles = useSessionStore((state) => state.roles);
  const expiresAt = useSessionStore((state) => state.expiresAt);
  const expiredReason = useSessionStore((state) => state.expiredReason);

  const [pending, setPending] = useState(false);

  // Actions are stable across renders (zustand guarantees it), so reading them
  // once keeps them out of every effect's dependency list.
  const { grant, clear, expire } = useSessionStore.getState();

  /** Restore at boot. Runs exactly once; a second refresh would rotate the cookie for nothing. */
  const restored = useRef(false);
  useEffect(() => {
    if (!restoreOnMount || restored.current) return;
    restored.current = true;

    // Deliberately not cancelled on cleanup. The session lives in a module-level
    // store rather than component state, so applying the result after an unmount
    // is legal — and under StrictMode it is the only thing that works: the
    // mount→cleanup→remount cycle would discard the first refresh's result, while
    // the remount returns early on `restored`, leaving `status` at 'restoring'
    // forever and the login screen rendering nothing.
    void (async () => {
      try {
        grant(await api.authRefresh({}), now());
      } catch (error) {
        // 401 here is the ordinary "not signed in" case. Anything else is a
        // platform problem, and the login screen will surface it on the attempt.
        clear();
        if (!ApiError.is(error) || error.code !== 'UNAUTHENTICATED') {
          // Not thrown: boot must complete either way, or the app never renders.
          console.warn('Session restore failed', ApiError.is(error) ? error.code : error);
        }
      }
    })();
  }, [api, clear, grant, now, restoreOnMount]);

  /** Keep alive. Re-armed whenever the token changes, cleared on unmount. */
  useEffect(() => {
    if (status !== 'authenticated' || expiresAt === null) return;

    const delay = Math.max(MIN_REFRESH_DELAY_MS, expiresAt - now() - REFRESH_MARGIN_MS);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          grant(await api.authRefresh({}), now());
        } catch {
          // The cookie was revoked or rotated out from under us. Ending the
          // session here — rather than waiting for the next request to 401 — is
          // what lets the UI say "you were signed out" instead of "that failed".
          expire();
          queryClient.clear();
        }
      })();
    }, delay);

    return () => clearTimeout(timer);
  }, [api, expire, expiresAt, grant, now, queryClient, status]);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      setPending(true);
      try {
        // A stale cache from a previous user in this tab must not survive a new
        // sign-in, and clearing before the request means there is no window in
        // which the new user could read the old data.
        queryClient.clear();
        grant(await api.authLogin(credentials), now());
      } finally {
        setPending(false);
      }
    },
    [api, grant, now, queryClient],
  );

  const register = useCallback(
    async (registration: RegisterRequest) => {
      setPending(true);
      try {
        queryClient.clear();
        grant(await api.authRegister(registration), now());
      } finally {
        setPending(false);
      }
    },
    [api, grant, now, queryClient],
  );

  const logout = useCallback(async () => {
    setPending(true);
    try {
      await api.authLogout({});
    } catch {
      // The local session ends regardless. A network failure must not leave a
      // user staring at a signed-in shell they asked to leave; the server-side
      // token expires on its own.
    } finally {
      clear();
      queryClient.clear();
      setPending(false);
    }
  }, [api, clear, queryClient]);

  const value = useMemo<AuthApi>(
    () => ({
      status,
      user,
      capabilities: capabilitySetFor(roles),
      sessionExpired: expiredReason === 'session-expired',
      login,
      register,
      logout,
      pending,
    }),
    [status, user, roles, expiredReason, login, register, logout, pending],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
